/**
 * NotFoundPage — Phase O.5 (purchase-web migration).
 *
 * Renders the ONE marketing 404 layout, `<MarketingNotFoundPage>` (PALM-WEB-404-MOBILE-OVERFLOW-001;
 * owner ruling Q-PW404-1 "Each app keeps its words"), with PurchasePuffin's own words and its two
 * destinations; its links wrap, then stack full width on a phone. (Its buttons had carried
 * `btn-primary` / `btn-secondary` without the `.btn` base, so they rendered 24px tall with no button
 * chrome.) The page owns its head, structured data and page view.
 *
 * @module portals/marketing-website/pages/NotFoundPage
 */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getBrand } from '@puffin-core/brand-assets';
import {
  MarketingNotFoundPage,
  MarketingSeo,
  useAnalytics,
  webPage,
  breadcrumbList,
} from '@puffin-core/marketing-shell';

import { PURCHASE_MARKETING_ROUTES } from '../constants/purchaseMarketingRoutePaths';

const APP_KEY = 'purchase';
// 🔴 `PAGE_TITLE` and `PAGE_DESCRIPTION` used to live here as English string literals, evaluated
// ONCE at import — before any user has a locale — so no `changeLanguage` could move them. They fed
// both the <MarketingSeo> head AND the JSON-LD, so a Spanish visitor got an English meta
// description in search results and English structured data. Invisible to
// `web/no-hardcoded-strings`, which sees JSX attributes, not module-scope constants. Now resolved
// at render from `notFound.seoTitle` / `notFound.seoDescription`.
const PAGE_CANONICAL = 'https://purchasepuffin.com/404';

/**
 * The 404's destinations; the first is the primary action.
 * @constant {Array<Object>}
 */
const QUICK_LINKS = [
  { to: PURCHASE_MARKETING_ROUTES.HOME, labelKey: 'notFound.homeLink' },
  { to: PURCHASE_MARKETING_ROUTES.CONTACT, labelKey: 'notFound.contactLink' },
];

/**
 * 404 page for unknown marketing-website routes. Renders the canonical
 * <MarketingSeo> head + JSON-LD and the ONE marketing 404 layout with two links.
 *
 * @returns {React.JSX.Element} The 404 page.
 */
function NotFoundPage() {
  // `getBrand` is a plain registry read (not a hook), so `brand` — and therefore
  // the jsonLd memo built from it — carries no hook dependency. The memo is
  // consequently placed FIRST, satisfying the canonical
  // state → effect → computed → custom ordering. It is evaluated at render time
  // (not module load), so test suites that stub `@puffin-core/marketing-shell`
  // still resolve `webPage` / `breadcrumbList` normally.
  const { t } = useTranslation();
  const { trackPageView } = useAnalytics();

  // The path it was reached at, under the title every marketing 404 reports (it reported a fixed
  // '/404' as 'Page Not Found', so its 404s could not be told apart or counted with the other sites').
  useEffect(() => {
    trackPageView(window.location.pathname, '404 Not Found');
  }, [trackPageView]);

  const brand = getBrand(APP_KEY);

  // Single SEO derivation point — title, description and JSON-LD recompute together when the
  // translator's identity changes (which tracks the active language), so the structured data can
  // never disagree with the rendered head. Mirrors payday-web's NotFoundPage, whose hook order
  // (custom hooks, then effect, then computed) lints clean under `web/component-state-order`.
  const seo = useMemo(() => {
    const title = t('notFound.seoTitle');
    const description = t('notFound.seoDescription');
    return {
      title,
      description,
      jsonLd: [
        webPage({
          appKey: APP_KEY,
          brand,
          url: PAGE_CANONICAL,
          title,
          description,
        }),
        breadcrumbList({
          crumbs: [
            { name: 'Home', url: 'https://purchasepuffin.com/' },
            { name: 'Not Found', url: PAGE_CANONICAL },
          ],
        }),
      ],
    };
  }, [brand, t]);

  const links = useMemo(
    () => QUICK_LINKS.map((link) => ({ href: link.to, label: t(link.labelKey) })),
    [t],
  );

  const ogImageUrl = `https://purchasepuffin.com${brand.assets.ogCard}`;

  return (
    <>
      <MarketingSeo
        title={seo.title}
        description={seo.description}
        canonical={PAGE_CANONICAL}
        ogType="website"
        ogImage={ogImageUrl}
        jsonLd={seo.jsonLd}
      />
      <MarketingNotFoundPage
        code={t('notFound.code')}
        title={t('notFound.title')}
        message={t('notFound.description')}
        links={links}
        linksAriaLabel={t('notFound.linksAriaLabel')}
      />
    </>
  );
}

export default NotFoundPage;
